/**
 * Query key factory for idea-related queries
 * Following React Query best practices for granular caching
 */

import { QueryClient } from '@tanstack/react-query';

export const ideaKeys = {
  // Base keys
  all: ['idea'] as const,
  lists: () => [...ideaKeys.all, 'list'] as const,
  details: () => [...ideaKeys.all, 'detail'] as const,
  
  // Specific idea queries
  detail: (ideaId: string) => [...ideaKeys.details(), ideaId] as const,
  evidences: (ideaId: string) => [...ideaKeys.detail(ideaId), 'evidences'] as const,
  matches: (ideaId: string) => [...ideaKeys.detail(ideaId), 'matches'] as const,
  
  // Filtered/searched evidences with proper cache key structure
  filteredEvidences: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sort?: { field: string; direction: 'asc' | 'desc' };
    search?: string;
    inclusion?: string;
  }) => [...ideaKeys.detail(ideaId), 'filtered-evidences', params] as const,
  
  // Separate data source query keys
  assignedEvidences: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'assigned-evidences', params] as const,

  assignedEvidencesInfinite: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'assigned-evidences-infinite', params] as const,

  tabEvidenceTotals: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sort?: { field: string; direction: 'asc' | 'desc' };
    inclusion?: string;
  }) => [...ideaKeys.detail(ideaId), 'tab-evidence-totals', params] as const,
  
  suggestedEvidences: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'suggested-evidences', params] as const,

  suggestedEvidencesInfinite: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sectionFilters?: any[];
    activeTab?: string;
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'suggested-evidences-infinite', params] as const,

  suggestedEvidencesTabCount: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    sectionFilters?: any[];
    activeTab?: string;
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'suggested-evidences-tab-count', params] as const,
  
  additionalEvidences: (ideaId: string, params: {
    filters?: any[];
    trendFilters?: any[];
    search?: string;
    sort?: { field: string; direction: 'asc' | 'desc' };
  }) => [...ideaKeys.detail(ideaId), 'additional-evidences', params] as const,
  
  // Perspective counts from backend API (single call for all perspectives)
  perspectiveCounts: (ideaId: string, params?: {
    currentPerspective?: string;
    filters?: any[];
    configs?: string[];
  }) => [...ideaKeys.detail(ideaId), 'perspective-counts', params || {}] as const,

  singlePerspectiveCount: (
    ideaId: string,
    params: { perspectiveKey: string; filtersKey: string }
  ) => [...ideaKeys.detail(ideaId), 'perspective-count', params] as const,
  
  // Themes and aggregations
  // Note: evidenceIds NOT in cache key to prevent re-running when evidenceIds change
  // evidenceIds are only used inside queryFn when ML API needs to be called
  // recalculateThemes is included to separate normal and recalculate queries
  themes: (ideaId: string, activeFilters?: string, recalculateThemes?: boolean) => [...ideaKeys.detail(ideaId), 'themes', activeFilters, recalculateThemes] as const,
  recalculateThemes: (ideaId: string, activeFilters?: string) =>
    [...ideaKeys.detail(ideaId), 'recalculate-themes', activeFilters] as const,
  aggregations: (evidenceIds: string[]) => [...ideaKeys.all, 'aggregations', evidenceIds.sort()] as const,
  MLthemes: (ideaId: string, evidenceIds?: string[], activeFilters?: string, recalculateThemes?: boolean) => [...ideaKeys.detail(ideaId), 'themes', evidenceIds?.sort(), activeFilters, recalculateThemes] as const,
  
  // Impact calculations
  impact: (ideaId: string, params?: {
    filters?: string;
    inclusion?: string;
  }) => [...ideaKeys.detail(ideaId), 'impact', params || {}] as const,
  
  // Activity logs
  activityLogs: (ideaId: string) => [...ideaKeys.detail(ideaId), 'activity-logs'] as const,

  // Sub-ideas (for parent idea page)
  subIdeas: (ideaId: string) => [...ideaKeys.detail(ideaId), 'sub-ideas'] as const,

  // Product status history (for trend graph)
  productStatusHistory: (ideaId: string) => [...ideaKeys.detail(ideaId), 'product-status-history'] as const,

  //Aggregations for evidences (not idea-specific)
  evidencesAggregations: (evidenceIds: string[]) => [
    'evidences-aggregations',
    evidenceIds?.length ? evidenceIds.sort() : []
  ] as const,  
  // Global configurations (not idea-specific)
  config: (configKey?: string) => configKey 
    ? [...ideaKeys.all, 'config', configKey] as const
    : [...ideaKeys.all, 'config'] as const,
  availableColumnSources: (pageKey: string) =>
    [...ideaKeys.all, 'availableColumnSources', pageKey] as const,
  tabs: () => ['ui', 'tabs'] as const, // UI-related, not idea-specific

  // Helper to find idea in list cache
  findIdeaInListCache: (queryClient: QueryClient, ideaId: string) => {
    // Search through all ideas list caches
    const listCaches = queryClient.getQueriesData({
      queryKey: ['ideas', 'list']
    });
    
    for (const [, data] of listCaches) {
      const ideas = (data as any)?.ideas || [];
      const foundIdea = ideas.find((idea: any) => idea._id === ideaId);
      if (foundIdea) return foundIdea;
    }
    return null;
  },

  // Helper to apply a field patch to an idea wherever it sits in a list cache.
  // The counterpart of findIdeaInListCache: IdeasTable seeds detail(ideaId)
  // from the list row on navigation, so a row left stale by a detail-only
  // patch is written back over that patch.
  patchIdeaInListCache: (
    queryClient: QueryClient,
    ideaId: string,
    patch: Record<string, any>
  ) => {
    queryClient.setQueriesData({ queryKey: ['ideas', 'list'] }, (data: any) => {
      const ideas = data?.ideas;
      if (!Array.isArray(ideas)) return data;

      let changed = false;
      const nextIdeas = ideas.map((idea: any) => {
        if (idea?._id !== ideaId) return idea;
        changed = true;
        return { ...idea, ...patch };
      });

      return changed ? { ...data, ideas: nextIdeas } : data;
    });
  },

  // The same write-back for the joined product item. Several idea rows can link
  // one product item, so every matching row is patched, not the first.
  patchProductItemInListCache: (
    queryClient: QueryClient,
    productItemId: string,
    patch: Record<string, any>
  ) => {
    queryClient.setQueriesData({ queryKey: ['ideas', 'list'] }, (data: any) => {
      const ideas = data?.ideas;
      if (!Array.isArray(ideas)) return data;

      let changed = false;
      const nextIdeas = ideas.map((idea: any) => {
        const productItem = idea?.productItem;
        const id = productItem?._id ?? productItem?.id;
        if (!productItem || String(id) !== productItemId) return idea;
        changed = true;
        return { ...idea, productItem: { ...productItem, ...patch } };
      });

      return changed ? { ...data, ideas: nextIdeas } : data;
    });
  }
}
